import { supabase } from '@/lib/supabase';
import { Listing } from '@/components/JobCard';
import { crmSync } from '@/contexts/AuthContext';
import { Lang, LEAD_PRICE, UserType } from '@/data/constants';



export const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);


export const buildSlug = (trade: string, city: string, state: string) =>
  `${trade}-${slugify(city)}-${state.toLowerCase()}-${Math.floor(1000 + Math.random() * 9000)}`;

/**
 * Columns the browse feed is allowed to pull.
 *
 * DATA MINIMISATION (anti-scraping review, item 8): the allow-list is now
 * enforced *server-side* inside the `browse-listings` edge function. This copy
 * is kept only as documentation of the contract.
 */
export const PUBLIC_LISTING_COLUMNS =
  'id,slug,title,description,trade,state,city,zip,budget_range,budget_min,budget_max,' +
  'target_date,project_type,source_type,photos,status,lead_price,language,created_at,' +
  'document_count,location_locked';

/** Hard ceiling on how many rows one request may pull (anti-harvesting). */
export const MAX_PAGE_SIZE = 60;

/**
 * Why this is a discriminated result and not just `Listing[]`
 * ----------------------------------------------------------
 * The mobile "Zero listings" launch blocker was caused by collapsing every
 * failure mode into `[]`: a rate-limit verdict, a network drop, a CORS failure
 * and a 5xx all produced an empty array, and the marketplace then rendered
 * "No jobs match your filters" — telling the user the marketplace was empty
 * when in fact 51 listings were published and the request had simply failed.
 *
 * Callers must now be able to tell these apart, so the UI can show the right
 * bilingual message plus a Retry button instead of a false empty state.
 */
export type BrowseStatus = 'ok' | 'rate_limited' | 'server_error' | 'network_error';

export interface BrowseResult {
  status: BrowseStatus;
  listings: Listing[];
  /** Seconds to wait before retrying (only meaningful for rate_limited). */
  retryAfter: number;
  /**
   * Machine/operator readable reason for a non-ok verdict ('timeout',
   * 'http_500', 'bad_shape', 'offline', ...). Logged, never shown raw.
   */
  detail?: string;
}

/** Raised when the browse endpoint throttles the caller. */
export interface BrowseError {
  rateLimited: boolean;
  retryAfter: number;
}

let lastBrowseError: BrowseError | null = null;
export const getLastBrowseError = () => lastBrowseError;

/**
 * Hard deadline for one browse request.
 *
 * This is NOT a fallback that invents data: when the deadline passes the caller
 * receives a real `network_error` verdict and the UI shows the error card with a
 * Retry button. Its only job is to guarantee that the promise ALWAYS settles, so
 * a hung connection (captive portal, dead socket, stalled gateway) can never
 * strand the page on "Loading projects…" forever.
 */
export const BROWSE_TIMEOUT_MS = 15000;

/**
 * Case/whitespace-insensitive city match.
 *
 * SINGLE SOURCE OF TRUTH: the marketplace page and the regression suite both
 * call this, so the filter that ships can never drift from the filter we test.
 * An empty needle matches EVERYTHING — a blank City box must never remove rows.
 */
export const matchesCity = (listingCity: string | null | undefined, needle: string): boolean => {
  const q = (needle || '').replace(/\s+/g, ' ').trim().toLowerCase();
  if (!q) return true;
  return (listingCity || '').replace(/\s+/g, ' ').trim().toLowerCase().includes(q);
};

/**
 * KEYWORD SEARCH — the fields a `?q=` term is allowed to match.
 *
 * PRIVACY: every entry below is a column that is ALREADY rendered on the public
 * card / detail page for a locked lead (see PUBLIC_LISTING_COLUMNS and
 * JobCard). The homeowner's name, phone, email, street address, ZIP, internal
 * notes, uploaded document paths and purchased-lead data are NOT in the browse
 * payload at all, so they are structurally impossible to search here — the
 * filter runs in the browser on rows the `browse-listings` edge function has
 * already stripped server-side.
 *
 * Deliberately EXCLUDED even though present on the row: `id`, `slug`,
 * `owner_id` and `created_at` — matching an opaque identifier is useless to a
 * contractor and lets someone probe for a specific record.
 */
export const KEYWORD_SEARCH_FIELDS = [
  'title',
  'description',
  'trade',
  'city',
  'state',
  'project_type',
  'budget_range',
] as const;

/** Lower-cased, accent-folded, whitespace-collapsed form used on both sides. */
const normalize = (s: unknown): string =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * Case/accent-insensitive keyword match across the PUBLIC fields above.
 *
 * SEMANTICS
 *  - An empty (or whitespace-only) term matches EVERYTHING. A blank search box
 *    must never be able to hide a real lead — the same invariant the City box
 *    already has, and the one the regression suite pins.
 *  - Multiple words are ANDed, and each may match a DIFFERENT field, so
 *    "roofing fontana" finds a roofing listing in Fontana. Order is irrelevant.
 *  - Hyphens/underscores in slugs are treated as spaces, so a search for
 *    "roofing" matches the stored trade slug `roofing` and "solar panel"
 *    matches `solar-panels`.
 *  - `extra` lets the caller add already-public display strings that are
 *    derived rather than stored — the localized trade label, for example, so a
 *    Spanish visitor searching "techado" matches the `roofing` slug.
 *
 * Shared with the browse regression suite so the filter under test is literally
 * the filter that ships.
 */
export const matchesKeyword = (
  listing: Record<string, unknown>,
  needle: string,
  extra: Array<string | null | undefined> = [],
): boolean => {
  const terms = normalize(needle).split(' ').filter(Boolean);
  if (terms.length === 0) return true;

  const haystack = normalize(
    [
      ...KEYWORD_SEARCH_FIELDS.map((f) => listing?.[f]),
      ...extra,
    ]
      .filter((v) => v !== null && v !== undefined && v !== '')
      .join(' ')
      // slug separators behave as word breaks ("solar-panels" -> "solar panels")
      .replace(/[-_/]+/g, ' '),
  );

  return terms.every((term) => haystack.includes(term));
};


/**
 * Upper bound of a listing's budget, in dollars, or null when unknown.
 *
 * AUDIT FIX: the marketplace used to do
 *   Number(budget_range.replace(/[^0-9]/g,'').slice(0,6))
 * which turns "$5,000 - $15,000" into 500001 — every listing passed every
 * budget filter, so the control did nothing. Real columns first
 * (budget_max / budget_min), then the largest number in the free-text range.
 */
export const listingBudgetCeiling = (listing: {
  budget_max?: number | null;
  budget_min?: number | null;
  budget_range?: string | null;
}): number | null => {
  if (typeof listing.budget_max === 'number' && listing.budget_max > 0) return listing.budget_max;
  if (typeof listing.budget_min === 'number' && listing.budget_min > 0) return listing.budget_min;
  const nums = (listing.budget_range || '')
    .replace(/,/g, '')
    .match(/\d+/g);
  if (!nums || nums.length === 0) return null;
  return Math.max(...nums.map(Number));
};

/**
 * Minimum-budget filter. A listing with NO budget information on file is kept:
 * hiding a real lead because the homeowner skipped an optional field would cost
 * contractors work. Shared with the regression suite.
 */
export const matchesMinBudget = (
  listing: { budget_max?: number | null; budget_min?: number | null; budget_range?: string | null },
  minBudget: string | number,
): boolean => {
  const floor = Number(minBudget) || 0;
  if (floor <= 0) return true;
  const ceiling = listingBudgetCeiling(listing);
  if (ceiling === null) return true;
  return ceiling >= floor;
};


/**
 * Public browse feed — full verdict.
 *
 * SECURITY: `mkt_listings_public` no longer has SELECT granted to anon or
 * authenticated, so PostgREST (which cannot be rate limited) is no longer a
 * usable scraping surface. Every public read goes through the `browse-listings`
 * edge function, which enforces a 60-row cap, per-IP throttling with escalating
 * lockout, security-event logging and a strict column allow-list.
 *
 * CONTRACT: this function NEVER throws and NEVER hangs. Callers can rely on it
 * settling with one of the four verdicts, which is what lets the page guarantee
 * it always leaves the loading state.
 */
export const fetchListingsResult = async (filters?: {
  trade?: string;
  city?: string;
  state?: string;
  type?: string;
  limit?: number;
  offset?: number;
}): Promise<BrowseResult> => {
  lastBrowseError = null;
  const size = Math.min(Math.max(filters?.limit ?? MAX_PAGE_SIZE, 1), MAX_PAGE_SIZE);

  // Offline is a distinct, recoverable state — never an empty marketplace.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { status: 'network_error', listings: [], retryAfter: 0, detail: 'offline' };
  }

  try {
    let data: { listings?: Listing[]; error?: string; retryAfter?: number } | null = null;
    let transportFailed = false;
    let detail = '';

    try {
      const call = supabase.functions.invoke('browse-listings', {
        body: {
          trade: filters?.trade || undefined,
          state: filters?.state || undefined,
          type: filters?.type || undefined,
          limit: size,
          offset: Math.max(filters?.offset ?? 0, 0),
        },
      });

      // Guarantees the await below settles even if the socket never answers.
      const timeout = new Promise<'__timeout__'>((resolve) =>
        setTimeout(() => resolve('__timeout__'), BROWSE_TIMEOUT_MS),
      );

      const res = await Promise.race([call, timeout]);
      if (res === '__timeout__') {
        console.error('[marketplace] browse-listings timed out after', BROWSE_TIMEOUT_MS, 'ms');
        return { status: 'network_error', listings: [], retryAfter: 0, detail: 'timeout' };
      }

      data = ((res as { data?: unknown }).data || null) as typeof data;
      const err = (res as { error?: unknown }).error;
      // supabase-js surfaces CORS / DNS / offline / non-2xx as `error`.
      if (err && !data) {
        transportFailed = true;
        const ctx = (err as { context?: { status?: number } })?.context;
        detail = `http_${ctx?.status ?? 'transport'}`;
        console.error('[marketplace] browse-listings transport failure:', detail, err);
      }
    } catch (e) {
      transportFailed = true;
      detail = 'exception';
      console.error('[marketplace] browse-listings threw:', e);
    }

    if (transportFailed) {
      return { status: 'network_error', listings: [], retryAfter: 0, detail: detail || 'transport' };
    }

    if (data?.error === 'rate_limited') {
      const retryAfter = Number(data.retryAfter || 60);
      lastBrowseError = { rateLimited: true, retryAfter };
      return { status: 'rate_limited', listings: [], retryAfter, detail: 'rate_limited' };
    }
    if (data?.error) {
      lastBrowseError = { rateLimited: false, retryAfter: 0 };
      console.error('[marketplace] browse-listings returned error:', data.error);
      return { status: 'server_error', listings: [], retryAfter: 0, detail: String(data.error) };
    }
    if (!data || !Array.isArray(data.listings)) {
      // Shape we do not recognise — treat as a server fault, NOT as "no results".
      console.error('[marketplace] browse-listings returned an unexpected shape:', data);
      return { status: 'server_error', listings: [], retryAfter: 0, detail: 'bad_shape' };
    }

    return { status: 'ok', listings: data.listings, retryAfter: 0 };
  } catch (e) {
    // Absolute backstop: this function must never reject, or the page that
    // awaits it can be stranded in its loading state with no error to show.
    console.error('[marketplace] fetchListingsResult fatal:', e);
    return { status: 'server_error', listings: [], retryAfter: 0, detail: 'fatal' };
  }
};


/**
 * Single source of truth for "what should the marketplace render?".
 *
 * Exported (rather than inlined in the page) so the permanent regression suite
 * can assert the invariant directly: it must be IMPOSSIBLE to return 'empty'
 * while the API returned listings, and impossible to return 'empty' for any
 * failure verdict.
 */
export const deriveFeedState = (
  status: BrowseStatus,
  visibleCount: number,
): BrowseStatus | 'empty' => {
  if (status !== 'ok') return status;
  return visibleCount === 0 ? 'empty' : 'ok';
};

/**
 * Array-returning convenience wrapper for secondary surfaces (home page teaser,
 * dashboard, "similar projects"). Those blocks simply render nothing on failure,
 * which is acceptable because they are not the primary browse experience.
 * The marketplace itself MUST use fetchListingsResult.
 */
export const fetchListings = async (filters?: Parameters<typeof fetchListingsResult>[0]) =>
  (await fetchListingsResult(filters)).listings;

/**
 * Single listing for the public detail page (ZIP / address / contact masked;
 * city + state are public). Like the feed loader, this NEVER throws and NEVER
 * hangs, so the detail page can always leave its loading state.
 */
export const fetchPublicListing = async (slug: string) => {
  try {
    const call = supabase.functions.invoke('browse-listings', { body: { slug } });
    const timeout = new Promise<'__timeout__'>((resolve) =>
      setTimeout(() => resolve('__timeout__'), BROWSE_TIMEOUT_MS),
    );
    const res = await Promise.race([call, timeout]);
    if (res === '__timeout__') {
      console.error('[marketplace] fetchPublicListing timed out for slug:', slug);
      return null;
    }
    const { data, error } = res as { data?: { listings?: Listing[] }; error?: unknown };
    if (error && !data) {
      console.error('[marketplace] fetchPublicListing transport failure:', error);
      return null;
    }
    const rows = (data?.listings as Listing[]) || [];
    return rows[0] || null;
  } catch (e) {
    console.error('[marketplace] fetchPublicListing threw:', e);
    return null;
  }
};





/**
 * Full listing row including city, zip and documents.
 * RLS only returns a row when the caller owns it, has purchased it, or is an
 * admin — so this is safe to call optimistically.
 */
export const fetchFullListing = async (id: string) => {
  const { data } = await supabase.from('mkt_listings').select('*').eq('id', id).maybeSingle();
  return (data as Listing) || null;
};

export const fetchPurchasedIds = async (buyerId?: string) => {
  if (!buyerId) return [] as string[];
  const { data } = await supabase.from('mkt_lead_purchases').select('listing_id').eq('buyer_id', buyerId);
  return (data || []).map((r: { listing_id: string }) => r.listing_id);
};


/**
 * Every lead on the marketplace costs the same fixed price.
 * Trade, project type, budget, city and poster are irrelevant to the price.
 * Kept async so existing call sites don't change.
 */
export const leadPriceFor = async (_trade?: string, _projectType?: string) => LEAD_PRICE;


/** Every lead may be shared with at most this many contractors. */
export const MAX_CONTRACTORS_PER_LEAD = 6;

export interface ClaimResult {
  ok: boolean;
  reason: string;
  purchaseId: string | null;
  remaining: number;
  slotsTaken: number;
  slotsMax: number;
}

/**
 * Unlock a lead. 1 CREDIT = 1 LEAD, MAX 6 CONTRACTORS PER LEAD.
 *
 * SECURITY: the browser can no longer write to mkt_lead_purchases at all — the
 * INSERT policy was removed, so a crafted REST call cannot self-grant
 * entitlement (which previously unlocked city/ZIP/contacts/PDFs for free).
 *
 * Everything happens inside `mkt_claim_lead`, which re-derives the buyer from
 * the JWT and delegates to `mkt_claim_lead_core` in ONE transaction:
 *   1. IDENTITY GATE — the core refuses any buyer that is not the JWT caller,
 *      so the user id can never be supplied by the client
 *   2. role + account status are re-checked server-side
 *   3. an existing entitlement short-circuits (never spends a second credit)
 *   4. LOCK 1 the listing, LOCK 2 the buyer's profile, LOCK 3 the subscription.
 *      The profile lock is what stops the SAME buyer spending one credit twice
 *      from two tabs / two devices on two DIFFERENT leads at the same instant.
 *   5. contractors on the lead are RE-COUNTED under the lock; at 6 of 6 the
 *      listing flips to status='sold_out' and the claim is refused — no credit
 *      is deducted and no 7th slot can ever be created
 *   6. exactly one credit is deducted with a CONDITIONAL update (plan allowance
 *      `WHERE leads_used < included_leads`, then a $29 credit
 *      `WHERE extra_credits > 0`), so a lost race can never push a balance
 *      below zero — and CHECK constraints reject it even if it tried
 *   7. the purchase is inserted ON CONFLICT DO NOTHING behind a unique index;
 *      if the insert loses a race, or the BEFORE INSERT capacity trigger
 *      rejects it, the credit is refunded in the same transaction
 *   8. reaching capacity raises the admin alert + activity log entry once
 *
 * The permanent proof for all of the above lives in the `credit-concurrency-test`
 * edge function (double clicks, multi-tab, multi-device, 14-way capacity storms,
 * raw REST bypass attempts, renewal/expiry) and is surfaced in the admin panel
 * under "Credits & capacity". Runs are stored in mkt_concurrency_runs.
 * If any step fails, nothing is granted and no credit is spent.
 */
export const claimLead = async (listingId: string): Promise<ClaimResult> => {
  const fail = (reason: string): ClaimResult => ({
    ok: false,
    reason,
    purchaseId: null,
    remaining: 0,
    slotsTaken: 0,
    slotsMax: MAX_CONTRACTORS_PER_LEAD,
  });

  const { data, error } = await supabase.rpc('mkt_claim_lead', { p_listing_id: listingId });
  if (error) return fail('error');
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return fail('error');
  return {
    ok: !!row.ok,
    reason: row.reason || '',
    purchaseId: row.purchase_id || null,
    remaining: row.remaining || 0,
    slotsTaken: row.slots_taken || 0,
    slotsMax: row.slots_max || MAX_CONTRACTORS_PER_LEAD,
  };
};

/** True when a lead has filled all of its contractor slots. */
export const isSoldOut = (listing?: Listing | null): boolean => {
  if (!listing) return false;
  if (listing.status === 'sold_out') return true;
  const max = listing.max_contractors || MAX_CONTRACTORS_PER_LEAD;
  return (listing.contractor_count || 0) >= max;
};


/**
 * Post-unlock bookkeeping (CRM sync only — the entitlement itself is already
 * written server-side by claimLead).
 *
 * `buyerType` MUST be the buyer's real role. canBuyLeads() is true for BOTH
 * subcontractors and general contractors, so this used to hard-code
 * 'subcontractor' and every GC that unlocked a lead arrived in the CRM tagged
 * as a sub — landing them in the wrong pipeline and the wrong automation.
 * Every other crmSync call site passes profile.user_type; this one now does too.
 *
 * ALSO pushes the "Purchased Lead" ACTIVITY into GoHighLevel (tags
 * "GigGone Lead Buyer" + "GigGone Purchased Lead", trade/city/state custom
 * fields) so the lead-purchase follow-up sequence can fire. Dynamic import
 * keeps marketplace.ts free of a static CRM dependency; failures are swallowed
 * because the lead is already unlocked and must never be undone by a CRM hiccup.
 */
export const recordPurchase = async (opts: {
  listing: Listing;
  buyerId: string;
  amount: number;
  buyerEmail: string;
  buyerName?: string;
  buyerPhone?: string;
  /** Real role of the buyer ('subcontractor' | 'gc'). */
  buyerType?: UserType;
  lang: Lang;
}) => {
  const { listing, buyerId, amount, buyerEmail, buyerName, buyerPhone, buyerType, lang } = opts;
  await crmSync({
    event: 'lead_purchased',
    email: buyerEmail,
    name: buyerName,
    phone: buyerPhone,
    language: lang,
    userType: buyerType || 'subcontractor',
    details: {
      Job: listing.title,
      Trade: listing.trade,
      Location: listing.state || '',
      Amount: `$${(amount / 100).toFixed(2)}`,
    },
  });

  try {
    const { crmActivity } = await import('@/lib/crmForms');
    await crmActivity('lead-purchased', {
      email: buyerEmail,
      name: buyerName,
      phone: buyerPhone,
      language: lang,
      userType: buyerType || 'subcontractor',
      userId: buyerId,
      city: listing.city || undefined,
      state: listing.state || undefined,
      trade: listing.trade || undefined,
      paid: true,
      paymentStatus: 'paid',
      fields: {
        listing_id: listing.id,
        listing_title: listing.title,
        listing_slug: listing.slug || '',
        project_type: listing.project_type || '',
        lead_amount: `$${(amount / 100).toFixed(2)}`,
      },
    });
  } catch (_e) {
    /* the lead is already unlocked — CRM retries server-side */
  }
};

/**
 * "Viewed Lead" activity. Fired once per lead per browser session from the
 * public detail page so GoHighLevel can score intent and run the
 * "you looked but did not unlock" follow-up. Never blocks the page.
 */
export const recordLeadView = async (opts: {
  listing: Listing;
  viewerEmail?: string | null;
  viewerName?: string;
  viewerPhone?: string;
  viewerType?: UserType;
  viewerId?: string;
  lang: Lang;
}) => {
  const { listing, viewerEmail, viewerName, viewerPhone, viewerType, viewerId, lang } = opts;
  if (!viewerEmail) return; // anonymous visitors have no CRM identity yet
  try {
    const { crmActivityOnce } = await import('@/lib/crmForms');
    crmActivityOnce('lead-viewed', `${viewerEmail}:${listing.id}`, {
      email: viewerEmail,
      name: viewerName,
      phone: viewerPhone,
      language: lang,
      userType: viewerType || 'subcontractor',
      userId: viewerId,
      city: listing.city || undefined,
      state: listing.state || undefined,
      trade: listing.trade || undefined,
      fields: {
        listing_id: listing.id,
        listing_title: listing.title,
        listing_slug: listing.slug || '',
        project_type: listing.project_type || '',
      },
    });
  } catch (_e) {
    /* view tracking is best-effort */
  }
};



export interface Contact {
  listing_id: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  address?: string;
}

export const fetchContact = async (listingId: string) => {
  const { data } = await supabase.from('mkt_listing_contacts').select('*').eq('listing_id', listingId).maybeSingle();
  return (data || null) as Contact | null;
};
